/**
 * Wholesale Comparison Report Renderer
 *
 * Multi-location table comparing Counter Wholesale performance
 * for the selected month with year-over-year variance.
 */

import React from 'react';

interface LocationMetrics {
  sales: number;
  cost: number;
  gross: number;
  gpPct: number | null;
}

interface LocationRow {
  locationId: number;
  locationName: string;
  current: LocationMetrics;
  prior: LocationMetrics;
  variance: { gross: number; pct: number | null };
}

export interface WholesaleComparisonData {
  locations: LocationRow[];
  totals: {
    current: LocationMetrics;
    prior: LocationMetrics;
    variance: { gross: number; pct: number | null };
  };
  year: number;
  month: number;
  monthName: string;
}

const fmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function formatCurrency(value: number): string {
  if (value < 0) {
    return `(${fmt.format(Math.abs(value))})`;
  }
  return fmt.format(value);
}

function formatPct(value: number | null): string {
  if (value === null) return '--';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

function formatGP(value: number | null): string {
  if (value === null) return '--';
  return `${value.toFixed(1)}%`;
}

function varianceColor(value: number): string {
  if (value > 0) return 'text-green-600';
  if (value < 0) return 'text-red-600';
  return 'text-gray-500';
}

interface Props {
  data: WholesaleComparisonData;
}

export const WholesaleComparisonReport: React.FC<Props> = ({ data }) => {
  const { locations, totals, year, monthName } = data;
  const priorYear = year - 1;

  return (
    <div className="wholesale-comparison-report">
      <style>{`
        @media print {
          @page { size: landscape; margin: 0.5in; }
          .wholesale-comparison-report {
            width: 100% !important;
            overflow: visible !important;
          }
          .wholesale-comparison-report table {
            width: 100% !important;
            font-size: 9pt !important;
            table-layout: fixed !important;
          }
          .wholesale-comparison-report td,
          .wholesale-comparison-report th {
            padding: 3px 4px !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }
        }
      `}</style>

      <div className="overflow-x-auto print:overflow-visible">
        <table className="w-full text-sm border-collapse">
          <thead>
            {/* Group header row */}
            <tr className="border-b-2 border-gray-300">
              <th className="text-left py-2 px-3 font-semibold text-gray-700" rowSpan={2}>
                Location
              </th>
              <th className="text-center py-1 px-1 font-semibold text-gray-700 border-l border-gray-200" colSpan={4}>
                {monthName} {year}
              </th>
              <th className="text-center py-1 px-1 font-semibold text-gray-500 border-l border-gray-200" colSpan={4}>
                {monthName} {priorYear}
              </th>
              <th className="text-center py-1 px-1 font-semibold text-gray-700 border-l border-gray-200" colSpan={2}>
                Variance
              </th>
            </tr>
            {/* Column header row */}
            <tr className="border-b border-gray-200 text-xs text-gray-500">
              <th className="text-right py-1 px-3 border-l border-gray-200">Sales</th>
              <th className="text-right py-1 px-3">Cost</th>
              <th className="text-right py-1 px-3">Gross</th>
              <th className="text-right py-1 px-3">GP%</th>
              <th className="text-right py-1 px-3 border-l border-gray-200">Sales</th>
              <th className="text-right py-1 px-3">Cost</th>
              <th className="text-right py-1 px-3">Gross</th>
              <th className="text-right py-1 px-3">GP%</th>
              <th className="text-right py-1 px-3 border-l border-gray-200">Gross</th>
              <th className="text-right py-1 px-3">%</th>
            </tr>
          </thead>
          <tbody>
            {locations.map((loc) => (
              <tr key={loc.locationId} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-2 px-3 text-gray-900 whitespace-nowrap">{loc.locationName}</td>
                <td className="py-2 px-3 text-right font-mono border-l border-gray-100">{formatCurrency(loc.current.sales)}</td>
                <td className="py-2 px-3 text-right font-mono text-gray-500">{formatCurrency(loc.current.cost)}</td>
                <td className="py-2 px-3 text-right font-mono font-medium">{formatCurrency(loc.current.gross)}</td>
                <td className="py-2 px-3 text-right font-mono text-gray-500">{formatGP(loc.current.gpPct)}</td>
                <td className="py-2 px-3 text-right font-mono border-l border-gray-100 text-gray-500">{formatCurrency(loc.prior.sales)}</td>
                <td className="py-2 px-3 text-right font-mono text-gray-400">{formatCurrency(loc.prior.cost)}</td>
                <td className="py-2 px-3 text-right font-mono text-gray-500">{formatCurrency(loc.prior.gross)}</td>
                <td className="py-2 px-3 text-right font-mono text-gray-400">{formatGP(loc.prior.gpPct)}</td>
                <td className={`py-2 px-3 text-right font-mono font-medium border-l border-gray-100 ${varianceColor(loc.variance.gross)}`}>
                  {formatCurrency(loc.variance.gross)}
                </td>
                <td className={`py-2 px-3 text-right font-mono ${varianceColor(loc.variance.gross)}`}>
                  {formatPct(loc.variance.pct)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-300 bg-gray-50 font-bold">
              <td className="py-2 px-3 text-gray-900">TOTAL</td>
              <td className="py-2 px-3 text-right font-mono border-l border-gray-100">{formatCurrency(totals.current.sales)}</td>
              <td className="py-2 px-3 text-right font-mono text-gray-500">{formatCurrency(totals.current.cost)}</td>
              <td className="py-2 px-3 text-right font-mono">{formatCurrency(totals.current.gross)}</td>
              <td className="py-2 px-3 text-right font-mono text-gray-500">{formatGP(totals.current.gpPct)}</td>
              <td className="py-2 px-3 text-right font-mono border-l border-gray-100 text-gray-500">{formatCurrency(totals.prior.sales)}</td>
              <td className="py-2 px-3 text-right font-mono text-gray-400">{formatCurrency(totals.prior.cost)}</td>
              <td className="py-2 px-3 text-right font-mono text-gray-500">{formatCurrency(totals.prior.gross)}</td>
              <td className="py-2 px-3 text-right font-mono text-gray-400">{formatGP(totals.prior.gpPct)}</td>
              <td className={`py-2 px-3 text-right font-mono border-l border-gray-100 ${varianceColor(totals.variance.gross)}`}>
                {formatCurrency(totals.variance.gross)}
              </td>
              <td className={`py-2 px-3 text-right font-mono ${varianceColor(totals.variance.gross)}`}>
                {formatPct(totals.variance.pct)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};
